import * as React from "react";

import "./suggestions-dialog.scss";

import {IconProp}           from "@fortawesome/fontawesome-svg-core";
import {faClockRotateLeft}  from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}    from "@fortawesome/react-fontawesome";

import {Types}      from "@Common/types";
import {upperCase1} from "@Common/utils/strings";

import {InternalLink}           from "@Xpresso/routes/common/components/internal-link";
import {TemplateSvgThumbnail}   from "@Xpresso/routes/common/components/template-svg-thumbnail/template-svg-thumbnail";

import {Popover, PopoverTargetPosition, PopoverXYAnchor, usePopoverPosition} from "./popover";
import {ROUTE_OMNISEARCH} from "@Xpresso/routes/omni-search/omni-search-route";

type Getter             = Types.Xpresso.Loaders.ContextNameMap['apis']['next']['omniSearch']['getSearchSuggestions'];
type ApiResponse        = Awaited<ReturnType<Getter>>;
type Suggestion         = Types.Apis.Payloads.Next.OmniSearch.GetSearchSuggestions.Suggestion;
type TemplateSuggestion = Types.Apis.Payloads.Next.OmniSearch.GetSearchSuggestions.TemplateSuggestion;

type Activity = (
    | {type: "template", query: string, data: {id: number, name: string}}
    | {type: "search", query: string, data: {q: string}}
);

export const SuggestionsDialog = React.forwardRef(({
    anchor,
    getSearchSuggestions,
    open,
    origin,
    recordActivity,
    searchQuery="",
}:{
    anchor                  : PopoverXYAnchor,
    getSearchSuggestions    : Getter,
    open                    : boolean,
    origin                  : PopoverTargetPosition,
    recordActivity         ?: (a: Activity) => void,
    searchQuery            ?: string,
}, ref: React.Ref<HTMLDivElement|null>) =>
{
    const [popoverDiv, setPopoverDiv] = React.useState<HTMLDivElement|null>(null);
    // @ts-ignore because Xpresso React is outdated and its types are weird
    React.useImperativeHandle(ref, () => popoverDiv);

    const callApi = React.useCallback(cached(debounced(getSearchSuggestions)), [getSearchSuggestions]);

    const [loading, setLoading]         = React.useState<string>(null);
    const [suggestions, setSuggestions] = React.useState<ApiResponse>(null);
    React.useEffect(() =>
    {
        setLoading(searchQuery);
        callApi({q: searchQuery})
        .then(result =>
        {
            if (!result || (typeof result !== "object")) {
                throw new Error(`Unexpected response: ${result}`);
            }
            setLoading(loading => (loading === searchQuery && setSuggestions(result), loading));
        })
        .catch(error =>
        {
            if (error === BOUNCED) return;
            console.error(error);
        })
        .finally(() => setLoading(loading => loading === searchQuery ? null : loading));
    }, [searchQuery]);

    const recordSuggestionClick = (suggestion: Suggestion) =>
    {
        if (!recordActivity) return;
        const query = searchQuery;
        switch (suggestion.type)
        {
            case "template":
                return recordActivity({
                    query,
                    type: "template",
                    data: {id: suggestion.data.id, name: suggestion.label}
                });
            case "search":
                return recordActivity({query, type: "search", data: {q: suggestion.data.q}});
            default:
                console.warn(`Missing SearchHistory case for suggestion type '${(suggestion as any).type}'`);
        }
    };

    const showExploreCta    = !!searchQuery;
    const recentsSlots      = Math.min(showExploreCta ? 3 : 4, suggestions?.recents.length ?? 0);
    const occupiedSlots     = 1 + +(showExploreCta || recentsSlots > 0) + (+showExploreCta) + recentsSlots;
    const availableSlots    = 10 - occupiedSlots;

    const popoverRect   = popoverDiv?.getBoundingClientRect();
    const containerRect = popoverDiv?.offsetParent.getBoundingClientRect();

    const position = usePopoverPosition({
        containerOffset : containerRect,
        popover         : {anchor, rect: popoverRect},
        target          : origin,
    });

    if (loading !== null) {
        return <Popover ref={setPopoverDiv} {...{open, position}}><SuggestionsDialogLoading/></Popover>;
    }
    return (
        <Popover ref={setPopoverDiv} {...{open, position}}>
            <div className="suggestions-dialog-686f70" tabIndex={0}>
                <section className="ads-and-templates">
                    <header>Products</header>
                    {suggestions?.templates.slice(0, 3).map((template, index) => (
                        <TemplateSuggestionRow
                            key={index}
                            index={index}
                            suggestion={template}

                            onClick={() => recordSuggestionClick(template)}
                        />
                    ))}
                </section>

                <section className="predictions" style={{gridRowEnd: availableSlots + 2}}>
                    <header>Suggestions</header>
                    {suggestions?.predictions.slice(0, availableSlots).map((prediction, index) => (
                        <SimpleSuggestionRow
                            key={index}
                            suggestion={prediction}

                            onClick={() => recordSuggestionClick(prediction)}
                        />
                    ))}
                </section>

                {(showExploreCta || recentsSlots > 0) && <hr/>}

                {suggestions?.recents.length > 0 && (<>
                    <section
                        className="recents"
                        style={{gridRow: `${availableSlots + 3} / ${showExploreCta ? 10 : 11}`}}
                    >
                        {suggestions.recents.slice(0, recentsSlots).map((recent, index) => (
                            <SimpleSuggestionRow
                                key={index}
                                icon={faClockRotateLeft}
                                iconTitle={`Recent ${recent.type}`}
                                suggestion={recent}

                                onClick={() => recordSuggestionClick(recent)}
                            />
                        ))}
                    </section>
                </>)}

                {showExploreCta && (
                    <InternalLink
                        to={`${ROUTE_OMNISEARCH.EXPLORE}?q=${encodeURIComponent(searchQuery)}`}
                        className="cta"

                        onClick={() => (recordActivity({
                            type: "search",
                            query: searchQuery,
                            data: {q: searchQuery}
                        }), true)}
                    >
                        See all results for <span className="highlight">"{searchQuery}"</span>
                    </InternalLink>
                )}
            </div>
        </Popover>
    );
});

const SimpleSuggestionRow = ({icon, iconTitle, suggestion, onClick: emitClick}:{
    icon       ?: IconProp,
    iconTitle  ?: string,
    suggestion  : Suggestion,

    onClick?: () => void,
}) =>
{
    const {label, type} = suggestion;
    const href = suggestionAsHref(suggestion);
    return (
        <article
            onClick={emitClick}
            title={label}
            className="simple-suggestion"
        >
            {href && <InternalLink to={href}/>}
            {icon && <FontAwesomeIcon icon={icon} className="icon" title={iconTitle}/>}
            <header>{label}</header>
            {type !== "search" && (
                <span className="context">{upperCase1(type)}</span>
            )}
        </article>
    );
};

const TemplateSuggestionRow = ({index, suggestion, onClick: emitClick}:{
    index: number,
    suggestion: TemplateSuggestion,

    onClick?: () => void,
}) =>
{
    const {data, label} = suggestion;
    const href = suggestionAsHref(suggestion);

    const [thumbnailSvgCode, setThumbnailSvgCode] = React.useState<string|null>(null);
    React.useEffect(() =>
    {
        if (data.thumbnailUrl.endsWith(".svg")) {
            fetch(data.thumbnailUrl).then(r => r.text()).then(setThumbnailSvgCode);
        } else {
            setThumbnailSvgCode(null);
        }
    }, [data.thumbnailUrl]);

    return (
        <article
            onClick={emitClick}
            title={label}
            className="template-card"
            style={{gridRow: `${(index * 3) + 2} / ${(index * 3) + 5}`}}
        >
            {href && <InternalLink to={href}/>}
            <div className="thumbnail">
                {thumbnailSvgCode ? (
                    <TemplateSvgThumbnail svgCode={thumbnailSvgCode}/>
                ) : (
                    <img src={data.thumbnailUrl}/>
                )}
            </div>
            <div className="info">
                <header>{label}</header>
                {data.finishedWidth && data.finishedHeight && (
                    <span title={`${data.finishedWidth} x ${data.finishedHeight}`}>
                        {data.finishedWidth} x {data.finishedHeight}
                    </span>
                )}
                <span title={data.styleName}>{data.styleName}</span>
            </div>
        </article>
    );
};

const SuggestionsDialogLoading = () => (
    <div className="suggestions-dialog-686f70 loading">
        <section className="ads-and-templates">
            <header>Loading...</header>
            <article className="template-card" style={{gridRow: "2 / 5"}}/>
            <article className="template-card" style={{gridRow: "5 / 8"}}/>
            <article className="template-card" style={{gridRow: "8 / 11"}}/>
        </section>
        <section className="predictions" style={{gridRowEnd: "9"}}>
            <header></header>
            <SuggestionLoadingPlaceholder/>
            <SuggestionLoadingPlaceholder/>
            <SuggestionLoadingPlaceholder/>
            <SuggestionLoadingPlaceholder/>
        </section>
        <hr/>
        <section className="recents" style={{gridRow: "10 / 11"}}>
            <SuggestionLoadingPlaceholder/>
        </section>
    </div>
);

const SuggestionLoadingPlaceholder = () =>
{
    const widthRef  = React.useRef(50 + (Math.random() * 150));
    const widthPx   = widthRef.current;

    const contextRef    = React.useRef(Math.random() > 0.66);
    const shouldContext = contextRef.current;

    return (
        <article className="simple-suggestion">
            <header><i className="text-placeholder" style={{width: `${widthPx}px`}}/></header>
            {shouldContext && <span className="context"><i className="text-placeholder"/></span>}
        </article>
    );
};

const cached = <F extends (...args:any[]) => Promise<any>,>(f:F) =>
{
    const store:Record<string, {ts: number, result: ReturnType<F>}> = {};
    return (...args: Parameters<F>) =>
    {
        const key = JSON.stringify(args);
        const ts  = Date.now();
        if (!(key in store) || store[key].ts < (ts - 60000)) {
            const result = f(...args) as ReturnType<F>;
            store[key] = {ts, result};
            result.catch(() => delete store[key]);
        }
        return store[key].result;
    };
};

const BOUNCED = Symbol("bounced");
const debounced = <F extends (...args:any[]) => Promise<any>,>(f:F) =>
{
    let rejectLastCall:(() => void) = null;
    return (...args:Parameters<F>) => new Promise((resolve, reject) =>
    {
        rejectLastCall?.();
        const timeoutId = window.setTimeout(() =>
        {
            rejectLastCall = null;
            f(...args).then(resolve).catch(reject);
        }, 500);
        rejectLastCall = () =>
        {
            window.clearTimeout(timeoutId);
            reject(BOUNCED);
        };
    }) as ReturnType<F>;
};

const suggestionAsHref = (suggestion: Suggestion) =>
{
    switch (suggestion.type)
    {
        case "search": return `${ROUTE_OMNISEARCH.EXPLORE}?q=${suggestion.data.q}`;
        case "template": return `/product-options/${suggestion.data.id}`;
        default: return null;
    }
};

